/**
 * Contract of the checkout client-error report (browser -> /api/checkout/client-error
 * -> one `[checkout-client-error]` line in the container log).
 *
 * Client-safe on purpose: imported by the browser reporter AND the server
 * sanitizer, so both sides agree on the vocabulary. No node imports here.
 *
 * Privacy contract: the report carries WHAT failed and WHERE, never WHO. Field
 * KEYS yes, field VALUES never. Everything free-form (Saleor messages, exception
 * messages) goes through `scrubDiagnosticText` on both sides.
 */

export const CHECKOUT_CLIENT_ERROR_ENDPOINT = "/api/checkout/client-error";

/** Hard cap for the request body, enforced by the route (413 above). */
export const CHECKOUT_CLIENT_ERROR_MAX_BODY_BYTES = 4096;

export const CHECKOUT_ERROR_STEPS = ["information", "payment", "confirmation", "checkout"] as const;
export type CheckoutErrorStep = (typeof CHECKOUT_ERROR_STEPS)[number];

export const CHECKOUT_ERROR_STAGES = [
	// Information step submit chain, in order.
	"validation",
	"email_update",
	"shipping_address_update",
	"delivery_options_calculate",
	"delivery_chain",
	"delivery_method_update",
	"delivery_refresh",
	"promo_confirm",
	"superseded",
	"submit_exception",
	// Information step background work.
	"address_sync",
	"delivery_options_load",
	// Payment step.
	"payment_start",
	// Whole checkout.
	"render_crash",
	"unhandled",
] as const;
export type CheckoutErrorStage = (typeof CHECKOUT_ERROR_STAGES)[number];

export const CHECKOUT_ERROR_KINDS = [
	/** Our own client-side validation stopped the submit. */
	"validation",
	/** Saleor (or our API) answered with field/domain errors. */
	"mutation",
	/** The request never produced a usable answer (network, GraphQL top-level, HTTP). */
	"transport",
	/** A guard stopped the flow on purpose (no method, unknown promo state). */
	"blocked",
	/** A newer buyer action made this submit obsolete. */
	"aborted",
	/** A thrown error. */
	"exception",
] as const;
type CheckoutErrorKind = (typeof CHECKOUT_ERROR_KINDS)[number];

/**
 * Field keys the log may name. An allowlist, not a pattern: a key-shaped string
 * such as a surname would pass any pattern. Our own form fields plus the Saleor
 * input fields the checkout mutations report errors on; anything else is logged
 * as "other".
 */
export const CHECKOUT_LOG_FIELD_KEYS = [
	"email",
	"password",
	"legalConfirmation",
	"address",
	"shippingMethod",
	"brTaxId",
	"firstName",
	"lastName",
	"companyName",
	"streetAddress1",
	"streetAddress2",
	"city",
	"cityArea",
	"postalCode",
	"countryArea",
	"country",
	"countryCode",
	"phone",
	"name",
	"shippingAddress",
	"billingAddress",
	"deliveryMethodId",
	"shippingMethodId",
	"lines",
	"quantity",
	"variantId",
	"promoCode",
	"voucherCode",
	"giftCard",
	"languageCode",
	"id",
	"checkoutId",
	"token",
	"metadata",
	"amount",
	"paymentGateway",
	"payment",
] as const;

type CheckoutSaleorErrorDetail = {
	code?: string | null;
	field?: string | null;
	message?: string | null;
};

/** What a caller hands to `reportCheckoutClientError`. */
export type CheckoutClientErrorInput = {
	/** Saleor checkout id. The server logs only a short hash of it. */
	checkoutId?: string | null;
	channel?: string | null;
	step: CheckoutErrorStep;
	stage: CheckoutErrorStage;
	kind: CheckoutErrorKind;
	/** Machine-readable reason, e.g. "session_http", "chainFailed", "init_error". */
	reason?: string | null;
	/** Names of the fields involved, e.g. ["email", "postalCode"]. Never values. */
	fieldKeys?: readonly string[];
	/** Saleor field/domain errors: code, field key and a scrubbed short message. */
	saleorErrors?: readonly CheckoutSaleorErrorDetail[];
	/** Top-level GraphQL error codes (extensions.code). */
	errorCodes?: readonly (string | null | undefined)[];
	/** Top-level GraphQL / network error messages, scrubbed. */
	errorMessages?: readonly (string | null | undefined)[];
	/** The request failed at the network layer. */
	network?: boolean;
	/** HTTP status of a failed fetch, e.g. 403 from a handshake. */
	httpStatus?: number | null;
	provider?: string | null;
	paymentMethod?: string | null;
	/** Error class name of a thrown error, e.g. "TypeError". */
	errorName?: string | null;
	/** Scrubbed message of a thrown error. */
	errorMessage?: string | null;
	/** Top stack frames reduced to "file.js:line:col". */
	frames?: readonly string[];
	/** Submit attempt counter of the step. */
	attempt?: number | null;
};

/** What the browser actually sends: the input plus page context it can see. */
export type CheckoutClientErrorPayload = CheckoutClientErrorInput & {
	locale?: string | null;
	viewportWidth?: number | null;
};

/**
 * Removes anything in a diagnostic string that could carry buyer input.
 *
 * GraphQL coercion errors echo the offending variable value in quotes, Saleor
 * messages may quote an address part, and exception messages quote property
 * values. So: every quoted or bracketed literal goes, then e-mail addresses,
 * URLs (checkout links carry the id and payment secrets), long id/token-like
 * strings and every run of three or more digits (postal codes, phone numbers,
 * CPF/CNPJ, amounts). What stays is the static wording of the message, capped
 * to `max` characters. Unquoted names inside a sentence cannot be recognised;
 * Saleor's own messages are static and never contain one.
 */
export function scrubDiagnosticText(value: unknown, max = 160): string | null {
	if (typeof value !== "string") return null;
	const scrubbed = value
		.normalize("NFKC")
		.replace(/[\u0000-\u001f\u007f-\u009f\u2028\u2029]/g, " ")
		.replace(/"[^"]*"?|'[^']*'?|`[^`]*`?|“[^”]*”?|„[^“”]*[“”]?|«[^»]*»?|‹[^›]*›?|‘[^’]*’?/g, "[…]")
		.replace(/\{[^}]*\}?|\[(?!…\])[^\]]*\]?/g, "[…]")
		.replace(/\S+@\S+/g, "[email]")
		.replace(/\b[a-z][a-z0-9+.-]*:\/\/\S+/gi, "[url]")
		// Ids and tokens: base64 checkout ids, UUIDs, JWTs, payment secrets.
		.replace(/[A-Za-z0-9+/=_.:-]{20,}/g, "[id]")
		.replace(/\d(?:[\d\s().,/-]*\d){2,}/g, "[num]")
		.replace(/\s+/g, " ")
		.trim();
	if (!scrubbed) return null;
	return scrubbed.length > max ? `${scrubbed.slice(0, max - 1)}…` : scrubbed;
}
